import type { PlaceholderValue } from "@homeinstead/schemas";
import type {
  PortableTextComponentProps,
  PortableTextComponents,
} from "@portabletext/react";
import type { PortableTextListItemBlock } from "@portabletext/types";
import type { HeadingFont } from "./types";

import { cn } from "../../utils/classnames";
import { getBlockTextContent } from "./get-block-text-content";
import { hasBlockLevelMarks } from "./has-block-level-marks";
import {
  hasContainerWidthRichTextBlock,
  RICH_TEXT_CONTAINER_WIDTH_CLASS,
  RICH_TEXT_CONTAINER_WIDTH_GRID_CLASS,
} from "./has-container-width-rich-text-block";
import { RichTextGap } from "./RichTextGap";

const HEADING_FONT_CLASS: Record<HeadingFont, string> = {
  "editorial-new": "font-editorial-new font-light",
  jakarta: "font-jakarta font-bold",
};

type CreateDefaultBlockComponentsOptions = {
  activePlaceholderKeys?: readonly string[];
  headingFont?: HeadingFont;
  placeholders: Record<string, PlaceholderValue>;
};

function createDefaultBlockComponents({
  activePlaceholderKeys = [],
  headingFont = "jakarta",
  placeholders,
}: CreateDefaultBlockComponentsOptions): PortableTextComponents["block"] {
  const headingFontClass = HEADING_FONT_CLASS[headingFont];

  return {
    h1: ({ children }) => (
      <h1 className={`${headingFontClass} text-xl md:text-1xl`}>{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className={`${headingFontClass} text-xl`}>{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className={`${headingFontClass} text-lg`}>{children}</h3>
    ),
    h4: ({ children }) => (
      <h4 className="font-bold font-jakarta text-md">{children}</h4>
    ),
    h5: ({ children }) => (
      <h5 className="font-jakarta font-semibold text-base">{children}</h5>
    ),
    h6: ({ children }) => (
      <h6 className="font-jakarta font-semibold text-sm">{children}</h6>
    ),
    normal({ children, value }) {
      const textContent = getBlockTextContent(value?.children);
      const isEmpty = !textContent;

      if (isEmpty) {
        return <RichTextGap />;
      }

      if (hasBlockLevelMarks(value)) {
        // Portable Text injected by a mark is nested inside this span. Keep
        // container-width descendants connected to the outer wrapper's tracks.
        const needsContainerWidthGrid = hasContainerWidthRichTextBlock(
          [value],
          {
            activePlaceholderKeys,
            placeholders,
          },
        );

        return (
          <span
            className={cn("block", {
              [`${RICH_TEXT_CONTAINER_WIDTH_CLASS} ${RICH_TEXT_CONTAINER_WIDTH_GRID_CLASS}`]:
                needsContainerWidthGrid,
            })}
          >
            {children}
          </span>
        );
      }

      return <p>{children}</p>;
    },
  };
}

function createDefaultListComponents(): PortableTextComponents["list"] {
  return {
    bullet: ({ children }) => (
      <ul className="my-4 list-disc space-y-2 pl-4">{children}</ul>
    ),
    number: ({ children }) => (
      <ol className="my-4 list-decimal space-y-2 pl-4">{children}</ol>
    ),
  };
}

function isNestingPlaceholderItem(value: PortableTextListItemBlock): boolean {
  // Matches only items synthesized by @portabletext/toolkit when a list of a
  // different type restarts below level 1: their sole content is the nested
  // list. Authored items always carry at least one span, so empty authored
  // items keep their markers.
  const children: readonly { _type?: string }[] = value.children;

  return (
    Boolean(value._key?.endsWith(`-placeholder-${value.level ?? 1}`)) &&
    children.every((child) => child._type === "@list")
  );
}

function renderListItem({
  children,
  value,
}: PortableTextComponentProps<PortableTextListItemBlock>) {
  return (
    <li className={cn(isNestingPlaceholderItem(value) && "list-none")}>
      {children}
    </li>
  );
}

function createDefaultListItemComponents(): PortableTextComponents["listItem"] {
  return {
    bullet: renderListItem,
    number: renderListItem,
  };
}

export {
  createDefaultBlockComponents,
  createDefaultListComponents,
  createDefaultListItemComponents,
};
